import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useAuthStore } from './store/authStore';
import { authService } from './services/authService';
import { UserRole } from './types/auth';
import { MainLayout } from './components/layout/MainLayout';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { ProviderProvider } from './contexts/ProviderContext';
import { LoginPage } from './pages/LoginPage';
import { SSOCallbackPage } from './pages/SSOCallbackPage';
import { DashboardPage } from './pages/DashboardPage';
import { CostExplorerPage } from './pages/CostExplorerPage';
import { RecommendationsPage } from './pages/RecommendationsPage';
import RecommendationDetailPage from './pages/RecommendationDetailPage';
import { WhatIfSimulatorPage } from './pages/WhatIfSimulatorPage';
import { PolicyEditorPage } from './pages/PolicyEditorPage';
import { TaggingCompliancePage } from './pages/TaggingCompliancePage';
import AnomaliesPage from './pages/AnomaliesPage';
import AnomalyDetailPage from './pages/AnomalyDetailPage';
import { AdminConsolePage } from './pages/AdminConsolePage';
import DemoScenariosPage from './pages/DemoScenariosPage';
import { ChatHistoryPage } from './pages/ChatHistoryPage';
import { InvoicePage } from './pages/InvoicePage';
import { LoadingState } from './components/common/LoadingState';

/**
 * Main App component
 * Maps to task 1.15.1: React+TypeScript+Tailwind shell with auth flow and role-based navigation
 * WCAG 2.1 AA compliant
 * 
 * Role-based access:
 * - Admin: full access to all views including Admin Console
 * - Analyst: can view all dashboards + acknowledge anomalies (no admin, no tag editing)
 * - Viewer: read-only access to all dashboards (no admin, no edits)
 */

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const App: React.FC = () => {
  // Deliberately does NOT read the auth store's `isLoading`.
  //
  // This gate used to be `initializing || isLoading`, which froze the browser
  // on every SSO sign-in that lost a race:
  //   1. SSOCallbackPage mounts and calls completeSSOLogin(code)
  //   2. that sets isLoading = true on the store
  //   3. this gate then discarded the whole <Routes> tree, UNMOUNTING the very
  //      component whose work it was waiting for
  //   4. isLoading goes false -> routes render again -> the URL is still
  //      /auth/callback, so SSOCallbackPage REMOUNTS as a new instance with a
  //      fresh `ranOnce` ref and retries the same authorization code
  //   5. handleSSOCallback deletes the single-use PKCE verifier on its first
  //      read, so the retry threw "Missing PKCE verifier" INSTANTLY - no
  //      network wait - and the remount/retry cycle spun as fast as React
  //      could render, hanging the tab ("This page isn't responding", F12
  //      unable to open).
  //
  // It only ever worked because a fast, successful exchange called
  // navigate('/dashboard') before the remount, changing the URL so
  // SSOCallbackPage was not re-rendered. A slow first login lost that race.
  //
  // `initializing` is this component's OWN state for the one-time session
  // check below, and is the only thing this gate should wait on. The store's
  // isLoading tracks individual auth operations (login, logout, callback) and
  // the pages that start those render their own loading states.
  const setUser = useAuthStore((s) => s.setUser);
  const [initializing, setInitializing] = React.useState(true);

  // Check for existing session on mount
  useEffect(() => {
    const initAuth = async () => {
      try {
        const user = await authService.getCurrentUser();
        setUser(user);
      } catch (error) {
        setUser(null);
      } finally {
        setInitializing(false);
      }
    };

    initAuth();
  }, [setUser]);

  if (initializing) {
    return <LoadingState message="Initializing application..." />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/auth/callback" element={<SSOCallbackPage />} />

          {/* Standalone, unchromed utility page - deliberately outside
              MainLayout so it can be driven from a second browser tab while
              the app being demonstrated stays put in the first. Still admin-
              gated; unchromed does not mean unauthenticated. */}
          <Route
            path="/demo-scenarios"
            element={
              <ProtectedRoute requiredRoles={[UserRole.ADMIN]}>
                <DemoScenariosPage />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <ProviderProvider>
                  <MainLayout />
                </ProviderProvider>
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="cost-explorer" element={<CostExplorerPage />} />
            <Route path="recommendations" element={<RecommendationsPage />} />
            <Route path="recommendations/:id" element={<RecommendationDetailPage />} />
            <Route path="what-if-simulator" element={<WhatIfSimulatorPage />} />
            <Route path="policies" element={<PolicyEditorPage />} />
            <Route path="tagging" element={<TaggingCompliancePage />} />
            <Route path="anomalies" element={<AnomaliesPage />} />
            <Route path="anomalies/:id" element={<AnomalyDetailPage />} />
            <Route path="chat-history" element={<ChatHistoryPage />} />
            <Route path="invoice" element={<InvoicePage />} />
            <Route
              path="admin"
              element={
                <ProtectedRoute requiredRoles={[UserRole.ADMIN]}>
                  <AdminConsolePage />
                </ProtectedRoute>
              }
            />
          </Route>

          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
};

export default App;
